import { createEnv } from "@t3-oss/env-nextjs"
import { z } from "zod"

// On Vercel preview deployments, override BETTER_AUTH_URL (server-side) to match
// the preview origin. NEXT_PUBLIC_SITE_URL (client-side) is handled in next.config.ts
// because Next.js inlines NEXT_PUBLIC_* at compile time before env.ts runs.
if (process.env.VERCEL_ENV === "preview" && process.env.VERCEL_URL) {
  process.env.BETTER_AUTH_URL = `https://${process.env.VERCEL_URL}`
}

// Preview-origin validators: reject anything outside `*.preview.webscraping.app`
// at boot so a malformed Cloud Run env var fails the deploy instead of silently
// producing an untrusted-origin auth failure later.
const previewHostSchema = z
  .string()
  .regex(
    /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.preview\.webscraping\.app$/i,
    "PREVIEW_HOST must be a bare *.preview.webscraping.app hostname",
  )
  .optional()
const previewUrlSchema = z
  .url()
  .refine(v => {
    try {
      const u = new URL(v)
      return (
        u.protocol === "https:" &&
        (u.hostname === "preview.webscraping.app" ||
          u.hostname.endsWith(".preview.webscraping.app"))
      )
    } catch {
      return false
    }
  }, "PREVIEW_URL must be an https://*.preview.webscraping.app origin")
  .transform(v => new URL(v).origin)
  .optional()

export const env = createEnv({
  shared: {
    PORT: z.coerce.number().default(8000),
    VERCEL_URL: z
      .string()
      .optional()
      .transform(v => (v ? `https://${v}` : undefined)),
  },

  /**
   * Specify your server-side environment variables schema here. This way you can ensure the app isn't
   * built with invalid env vars.
   */
  server: {
    NODE_ENV: z.enum(["development", "test", "production"]).default("development"),
    VERCEL_ENV: z.enum(["development", "preview", "production"]).default("development"),
    DATABASE_URL: z.string().min(1),
    DIRECTORY_AUTH_SECRET: z.string().min(32).optional(),
    CRON_SECRET: z.string().optional(),
    DIRECTORY_GITHUB_TOKEN: z.string().min(1).optional(),
    BETTER_AUTH_SECRET: z.string().min(1),
    BETTER_AUTH_URL: z.url().min(1),
    REDIS_URL: z.string().optional(),
    RESEND_API_KEY: z.string().min(1),
    RESEND_SENDER_EMAIL: z.email().min(1),
    RESEND_AUDIENCE_ID: z.string().min(1),
    RESEND_LEGAL_SEGMENT_ID: z.string().min(1),
    S3_ENDPOINT: z.string().optional(),
    S3_REGION: z.string().min(1),
    S3_BUCKET: z.string().min(1),
    S3_ACCESS_KEY: z.string().min(1),
    S3_SECRET_ACCESS_KEY: z.string().min(1),
    S3_PUBLIC_URL: z.string().optional(),
    SCREENSHOTONE_ACCESS_KEY: z.string().min(1),
    PLAUSIBLE_API_KEY: z.string().min(1),
    PLAUSIBLE_URL: z.url().min(1),
    AI_GATEWAY_API_KEY: z.string().optional(),
    AI_CHAT_MODEL: z.string().default("openai/gpt-4o"),
    AI_COMPLETION_MODEL: z.string().default("openai/gpt-4o-mini"),
    JINA_API_KEY: z.string().optional(),
    WEBHOOK_URL: z.url().optional(),
    WEBHOOK_TOKEN: z.string().optional(),
    SENTRY_AUTH_TOKEN: z.string().optional(),
    SENTRY_DSN: z.url().optional(),
    SENTRY_ENVIRONMENT: z.string().optional(),
    SENTRY_ORG: z.string().optional(),
    SENTRY_PROJECT: z.string().optional(),
    SENTRY_RELEASE: z.string().optional(),
    SENTRY_TRACES_SAMPLE_RATE: z.coerce.number().min(0).max(1).optional(),
    PREVIEW_MODE: z.enum(["true", "false"]).optional(),
    PREVIEW_URL: previewUrlSchema,
    PREVIEW_HOST: previewHostSchema,
  },

  /**
   * Specify your client-side environment variables schema here.
   * For them to be exposed to the client, prefix them with `NEXT_PUBLIC_`.
   */
  client: {
    NEXT_PUBLIC_PLAUSIBLE_URL: z.url().optional(),
    NEXT_PUBLIC_PLAUSIBLE_DOMAIN: z.string().optional(),

    NEXT_PUBLIC_SITE_URL: z.url().min(1),
    NEXT_PUBLIC_SITE_EMAIL: z.email().min(1),
    NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN: z.string().min(1),
    NEXT_PUBLIC_POSTHOG_HOST: z.url().min(1),
    NEXT_PUBLIC_SENTRY_DSN: z.url().optional(),
    NEXT_PUBLIC_SENTRY_ENVIRONMENT: z.string().optional(),
    NEXT_PUBLIC_SENTRY_RELEASE: z.string().optional(),
    NEXT_PUBLIC_SENTRY_TRACES_SAMPLE_RATE: z.coerce.number().min(0).max(1).optional(),
    NEXT_PUBLIC_LOGODEV_TOKEN: z.string().optional(),
  },

  /**
   * Destructure all variables from `process.env` to make sure they aren't tree-shaken away.
   */
  experimental__runtimeEnv: {
    NEXT_PUBLIC_PLAUSIBLE_URL: process.env.NEXT_PUBLIC_PLAUSIBLE_URL,
    NEXT_PUBLIC_PLAUSIBLE_DOMAIN: process.env.NEXT_PUBLIC_PLAUSIBLE_DOMAIN,

    PORT: process.env.PORT,
    VERCEL_URL: process.env.VERCEL_URL,
    NEXT_PUBLIC_SITE_URL: process.env.NEXT_PUBLIC_SITE_URL,
    NEXT_PUBLIC_SITE_EMAIL: process.env.NEXT_PUBLIC_SITE_EMAIL,
    NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN: process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN,
    NEXT_PUBLIC_POSTHOG_HOST: process.env.NEXT_PUBLIC_POSTHOG_HOST,
    NEXT_PUBLIC_SENTRY_DSN: process.env.NEXT_PUBLIC_SENTRY_DSN,
    NEXT_PUBLIC_SENTRY_ENVIRONMENT: process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT,
    NEXT_PUBLIC_SENTRY_RELEASE: process.env.NEXT_PUBLIC_SENTRY_RELEASE,
    NEXT_PUBLIC_SENTRY_TRACES_SAMPLE_RATE: process.env.NEXT_PUBLIC_SENTRY_TRACES_SAMPLE_RATE,
    NEXT_PUBLIC_LOGODEV_TOKEN: process.env.NEXT_PUBLIC_LOGODEV_TOKEN,
  },

  /**
   * Run `build` or `dev` with `SKIP_ENV_VALIDATION` to skip env validation.
   * This is especially useful for Docker builds.
   */
  skipValidation: !!process.env.SKIP_ENV_VALIDATION,

  /**
   * Makes it so that empty strings are treated as undefined.
   * `SOME_VAR: z.string()` and `SOME_VAR=''` will throw an error.
   */
  emptyStringAsUndefined: true,
})

export const isProd =
  process.env.NODE_ENV === "production" || process.env.VERCEL_ENV === "production"
export const isDev = !isProd

export const getS3PublicUrl = () =>
  env.S3_PUBLIC_URL ?? `https://${env.S3_BUCKET}.s3.${env.S3_REGION}.amazonaws.com`
